import { useState, useRef } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import _ from 'lodash'
import ReCAPTCHA from 'react-google-recaptcha'
import { FormProvider, useForm } from 'react-hook-form'



import { env } from '@/env'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { getErrorMessage, showErrorAlert } from '@/shared/lib/errors'
import { Button } from '@/shared/ui'
import { alert } from '@/shared/ui/dialog'

import { useAuthContext } from '../model/auth-context'
import { resetPasswordSchema } from '../model/schemas'
import { ConfirmationFields } from './fields/ConfirmationFields'
import { ResetPasswordFields } from './fields/ResetPasswordFields'

const isDev = env.NODE_ENV === 'development'

export const ResetPasswordForm = () => {
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const {
    httpClient,
    isLoading,
    setIsLoading,
    styleConfig,
    goToSignIn,
    reCaptchaEnabled,
    useReCaptchaV3,
    currentEmail,
    reCaptchaKey,
    config
  } = useAuthContext()
  const methods = useForm({
    resolver: zodResolver(resetPasswordSchema)
  })
  const [tokenValidated, setTokenValidated] = useState(false)

  const { t } = useTranslation()

  const handleValidateToken = async token => {
    if (!token || token.length < 6) return

    setIsLoading(true)
    setTokenValidated(false)

    try {
      await httpClient.post('/auth/reset-password-token-validation', {
        email: currentEmail,
        token
      })

      setTokenValidated(token)
    } catch (error) {
      const { errors } = getErrorMessage(error)

      if (_.has(errors, 'reset_password_token')) {
        void alert({
          variant: 'destructive',
          title: t('auth.invalidCode', {}, { fallback: 'Código inválido' }),
          description: t(
            'auth.invalidCodeMessage',
            {},
            {
              fallback:
                'O código informado é inválido, verifique se foi digitado corretamente e, caso precise, solicite um novo no link do formulário'
            }
          )
        })
      } else showErrorAlert(error)
    }

    setIsLoading(false)
  }
  const handleResetPassword = async data => {
    setIsLoading(true)

    // if (reCaptchaEnabled) {
    //   let recaptchaValue

    //   if (useReCaptchaV3) recaptchaValue = await reCaptchaRef.current.execute()
    //   else recaptchaValue = reCaptchaRef.current.getValue()

    //   if (!recaptchaValue) {
    //     Swal.fire({
    //       icon: 'error',
    //       title: 'Oops...',
    //       text: 'Antes de entrar, use o reCAPTCHA pra verificar sua identidade'
    //     })

    //     setIsLoading(false)
    //     return
    //   }
    // }

    const { password, password_confirmation } = data

    httpClient
      .put('/auth/reset-password', {
        email: currentEmail,
        token: tokenValidated,
        password,
        password_confirmation
      })
      .then(() => {
        void alert({
          variant: 'success',
          title: t('auth.done', {}, { fallback: 'Feito!' }),
          description: t(
            'auth.passwordResetSuccess',
            {},
            {
              fallback:
                'Senha resetada com sucesso, faça login usando sua nova senha'
            }
          )
        }).then(() => goToSignIn())
      })
      .catch(showErrorAlert)
      .finally(() => setIsLoading(false))
  }

  if (!currentEmail)
    return (
      <>{t('auth.emailNotPresent', {}, { fallback: 'E-mail não presente' })}</>
    )
  if (tokenValidated)
    return (
      <>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleResetPassword)}>
            <ResetPasswordFields />
            {reCaptchaEnabled && (
              <div className="flex items-center justify-center w-full">
                <ReCAPTCHA
                  ref={reCaptchaRef}
                  sitekey={reCaptchaKey}
                  size={useReCaptchaV3 ? 'invisible' : 'normal'}
                />
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              className="mb-10 mt-8 w-full"
              loading={isLoading}
              showTrailingIcon={false}
            >
              {t(
                isLoading ? 'buttons.resetting' : 'buttons.reset',
                {},
                { fallback: isLoading ? 'Resetando...' : 'Resetar senha' }
              )}
            </Button>
            <div className="flex justify-center text-center text-sm font-semibold text-gray-700">
              {t(
                'auth.passwordAlreadyReset',
                {},
                { fallback: 'Já resetou sua senha?' }
              )}
              <Button
                type="button"
                variant="link"
                className="ml-1 h-auto p-0 text-sm font-semibold"
                onClick={() => goToSignIn()}
              >
                {t('auth.login', {}, { fallback: 'Faça login' })}
              </Button>
            </div>
          </form>
        </FormProvider>
      </>
    )
  return (
    <ConfirmationFields
      onValidateToken={handleValidateToken}
      resendTokenApiUrl="/auth/reset-password"
    />
  )
}
